import axios from "axios";
import { API_IP } from "../env";
// Set your API's base URL
const API_URL = `${API_IP}/api`;

// Helper function to get the auth header
const getAuthHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Gets all bookings from the admin endpoint
 *
 */
export const getAdminBookings = (token, page = 1, limit = 15, search = "") => {
  // Add search as a query parameter
  return axios.get(
    `${API_URL}/admin?page=${page}&limit=${limit}&search=${encodeURIComponent(
      search
    )}`,
    getAuthHeaders(token)
  );
};

/**
 * Gets the per-day Schedule_Date rows for one booking. Used by the
 * Manager screen's expandable schedule panel.
 */
export const getBookingSchedule = (token, booking_id) => {
  return axios.get(
    `${API_URL}/admin/bookings/${booking_id}/schedule`,
    getAuthHeaders(token)
  );
};

/**
 * Aggregate booking detail — booking + patient + provider + service +
 * sub-services + schedule + e-transfer + computed pricing. Powers the
 * BookingDetailModal opened by clicking a row in ManagerScreen.
 */
export const getBookingDetail = (token, booking_id) => {
  return axios.get(
    `${API_URL}/admin/bookings/${booking_id}`,
    getAuthHeaders(token)
  );
};

/**
 * Bulk confirm or decline multiple bookings in one request. Returns a
 * per-row result list so the UI can display "Confirmed N, failed M".
 *
 * action = "confirm" | "decline"
 * Response: { action, total, succeeded, failed, results: [...] }
 */
export const bulkBookingAction = (token, booking_ids, action) => {
  return axios.post(
    `${API_URL}/admin/bookings/bulk`,
    { booking_ids, action },
    getAuthHeaders(token)
  );
};

/**
 * Paginated audit log read. Supports query filters: action, admin_id,
 * target_type, target_id, result, from, to, search.
 */
export const listAuditEntries = (token, opts = {}) => {
  return axios.get(`${API_URL}/admin/audit`, {
    ...getAuthHeaders(token),
    params: opts,
  });
};

/** Distinct actions + admin actors for populating filter dropdowns. */
export const listAuditFacets = (token) => {
  return axios.get(`${API_URL}/admin/audit/facets`, getAuthHeaders(token));
};

/**
 * Returns a map of date → array of busy hour slots for a provider in the
 * given date range. Used by the Provider Assignment modal to check whether
 * a candidate provider's schedule conflicts with the booking's time slots.
 *
 * Response shape: { "2026-06-05": [{ start_time: "07:00" }, ...], ... }
 */
export const getProviderAvailability = (token, provider_id, start_date, end_date) => {
  return axios.get(
    `${API_URL}/providers/availability-range`,
    {
      ...getAuthHeaders(token),
      params: { provider_id, start_date, end_date },
    }
  );
};

/**
 * Batch availability lookup for the Assign Provider modal. One HTTP request
 * covers every visible provider for a booking's date range. Replaces the
 * old N parallel calls to /providers/availability-range.
 *
 * Response shape:
 *   { "<provider_id>": { "2026-06-05": [{ start_time: "07:00" }, ...] }, ... }
 */
export const getProvidersAvailabilityBatch = (token, provider_ids, start_date, end_date) => {
  return axios.post(
    `${API_URL}/admin/providers/availability-batch`,
    { provider_ids, start_date, end_date },
    getAuthHeaders(token)
  );
};

/**
 * Gets all providers from the admin endpoint
 *
 */
export const getAllProviders = (token) => {
  return axios.get(`${API_URL}/admin/providers`, getAuthHeaders(token));
};

/**
 * Gets full detail for a single provider
 */
export const getProviderDetail = (token, provider_id) => {
  return axios.get(
    `${API_URL}/admin/providers/${provider_id}/detail`,
    getAuthHeaders(token)
  );
};

/**
 * Updates a booking's provider
 *
 */
export const updateBookingProvider = (token, booking_id, provider_id) => {
  const payload = {
    booking_id,
    provider_id, // This will trigger the provider change logic
  };
  return axios.patch(`${API_URL}/admin`, payload, getAuthHeaders(token));
};

/**
 * Confirms or Unconfirms a booking on behalf of a provider
 *
 * @param {string} token - Admin JWT
 * @param {string} booking_id - The booking to update
 * @param {boolean} acceptanceStatus - true to confirm, false to unconfirm
 */
export const updateBookingConfirmation = (
  token,
  booking_id,
  acceptanceStatus
) => {
  const payload = {
    booking_id,
    acceptanceStatus, // This will trigger the acceptance logic
  };
  return axios.patch(`${API_URL}/admin`, payload, getAuthHeaders(token));
};

/**
 * TestFlight: list beta testers for the configured app.
 */
export const getTestFlightTesters = (token, search = "") => {
  return axios.get(`${API_URL}/admin/testflight/testers`, {
    params: { search },
    ...getAuthHeaders(token),
  });
};

/**
 * TestFlight: list beta groups for the configured app.
 */
export const getTestFlightGroups = (token) => {
  return axios.get(
    `${API_URL}/admin/testflight/groups`,
    getAuthHeaders(token)
  );
};

/**
 * TestFlight: invite a new beta tester to a group.
 * @param {object} payload - { firstName, lastName, email, groupId }
 */
export const inviteTestFlightTester = (token, payload) => {
  return axios.post(
    `${API_URL}/admin/testflight/testers`,
    payload,
    getAuthHeaders(token)
  );
};

/**
 * TestFlight: resend the TestFlight invitation email to an existing tester.
 */
export const resendTestFlightInvite = (token, testerId) => {
  return axios.post(
    `${API_URL}/admin/testflight/testers/${testerId}/resend`,
    {},
    getAuthHeaders(token)
  );
};

/**
 * TestFlight: delete a beta tester from the app.
 */
export const deleteTestFlightTester = (token, testerId) => {
  return axios.delete(
    `${API_URL}/admin/testflight/testers/${testerId}`,
    getAuthHeaders(token)
  );
};

/**
 * e-Transfer reconciliation: list all pending Etransfer_Payment rows.
 */
export const getPendingEtransfers = (token) => {
  return axios.get(
    `${API_URL}/admin/etransfer/pending`,
    getAuthHeaders(token)
  );
};

/**
 * e-Transfer reconciliation: list inbox-log rows the Gmail poller could not auto-match.
 */
export const getUnmatchedInbox = (token) => {
  return axios.get(
    `${API_URL}/admin/etransfer/inbox/unmatched`,
    getAuthHeaders(token)
  );
};

/**
 * e-Transfer reconciliation: bind one or more unmatched inbox-log deposits to a
 * pending Etransfer_Payment. Server enforces strict equality
 * (sum(parsed_amount) === expected_amount) and flips the related booking to
 * paid. Pass a single-element array for the 1:1 case.
 */
export const manualConfirmEtransfer = (token, etransfer_id, inbox_log_ids) => {
  return axios.post(
    `${API_URL}/admin/etransfer/manual-confirm`,
    { etransfer_id, inbox_log_ids },
    getAuthHeaders(token)
  );
};
